import { useVirtualizer, type Virtualizer } from "@tanstack/react-virtual";
import { type RefObject, useCallback, useEffect, useRef, useState } from "react";

export type LoadingState = "idle" | "loading" | "loading-more" | "error";

export interface UseInfiniteScrollOptions<T = unknown> {
  /** Array of items being displayed */
  items: T[];
  /** Callback to load more items (optional - if not provided, no infinite scroll) */
  onLoadMore?: () => void | Promise<void>;
  /** Whether there are more items to load */
  hasMore?: boolean;

  // Loading state (auto-detect or explicit)
  /** Auto-detects initial vs loading-more from items.length */
  isLoading?: boolean;
  /** Explicit loading state override (takes precedence) */
  loadingState?: LoadingState;

  // Virtualization options
  /** Enable virtualization (default: true for 100+ items) */
  enableVirtualization?: boolean;
  /** Estimated size of each item in pixels */
  estimatedItemSize?: number;
  /** Number of items to render outside viewport */
  overscan?: number;

  // Scroll behavior
  /** Custom scroll element (defaults to parent) */
  scrollElement?: HTMLElement | null;
  /** Distance from bottom in pixels to trigger load */
  loadMoreThreshold?: number;
}

export interface UseInfiniteScrollReturn {
  // Virtualization (null if disabled)
  virtualizer: Virtualizer<HTMLElement, Element> | null;
  virtualItems: ReturnType<Virtualizer<HTMLElement, Element>["getVirtualItems"]>;

  // Computed loading state
  computedLoadingState: LoadingState;
  isInitialLoad: boolean;
  isLoadingMore: boolean;

  // Refs
  scrollRef: RefObject<HTMLDivElement | null>;
  loadMoreRef: RefObject<HTMLDivElement | null>;

  // Utils
  scrollToIndex: (index: number, options?: { align?: "start" | "center" | "end" }) => void;
  scrollToTop: () => void;
}

/**
 * useInfiniteScroll
 *
 * Hook for implementing infinite scroll with optional virtualization.
 * Intelligently detects initial vs loading-more states from context.
 *
 * Features:
 * - Smart loading state detection (initial vs loading-more)
 * - Optional explicit state control
 * - TanStack Virtual integration for performance
 * - Intersection Observer for load triggering
 * - Configurable virtualization threshold
 *
 * @example
 * ```tsx
 * const { scrollRef, computedLoadingState, isLoadingMore } = useInfiniteScroll({
 *   items,
 *   onLoadMore: fetchNextPage,
 *   hasMore: hasNextPage,
 *   isLoading: isFetching,
 * });
 * ```
 */
export function useInfiniteScroll<T = unknown>({
  items,
  onLoadMore,
  hasMore,
  isLoading = false,
  loadingState,
  enableVirtualization,
  estimatedItemSize = 50,
  overscan = 5,
  scrollElement,
  loadMoreThreshold = 100,
}: UseInfiniteScrollOptions<T>): UseInfiniteScrollReturn {
  const scrollRef = useRef<HTMLDivElement>(null);
  const loadMoreRef = useRef<HTMLDivElement>(null);
  const loadingRef = useRef(false);
  const [refsReady, setRefsReady] = useState(0);

  // Track when refs are populated
  useEffect(() => {
    if (loadMoreRef.current && (scrollElement || scrollRef.current)) {
      setRefsReady((prev) => prev + 1);
    }
  }, [scrollElement, items.length]); // Re-check when items change

  // Compute loading state (explicit takes precedence)
  const computedLoadingState: LoadingState = loadingState
    ? loadingState
    : isLoading
      ? items.length === 0
        ? "loading"
        : "loading-more"
      : "idle";

  const isInitialLoad = computedLoadingState === "loading";
  const isLoadingMore = computedLoadingState === "loading-more";

  // Auto-enable virtualization for large lists
  const shouldVirtualize = enableVirtualization !== undefined ? enableVirtualization : items.length >= 100;

  // Always call virtualizer hook (React rules), but conditionally expose it
  const tempVirtualizer = useVirtualizer({
    count: items.length,
    getScrollElement: () => scrollElement || scrollRef.current,
    estimateSize: () => estimatedItemSize,
    overscan,
  });

  // Only expose virtualizer if enabled
  const virtualizer = shouldVirtualize ? tempVirtualizer : null;
  const virtualItems = shouldVirtualize ? tempVirtualizer.getVirtualItems() : [];

  // Intersection Observer for load trigger
  useEffect(() => {
    const target = loadMoreRef.current;
    const scrollContainer = scrollElement || scrollRef.current;

    // Wait until both target and scroll container are available, and onLoadMore is provided
    if (!target || !scrollContainer || !hasMore || isLoading || !onLoadMore) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry?.isIntersecting && !loadingRef.current && hasMore && onLoadMore) {
          loadingRef.current = true;
          Promise.resolve(onLoadMore()).finally(() => {
            loadingRef.current = false;
          });
        }
      },
      {
        root: scrollContainer,
        rootMargin: `0px 0px ${loadMoreThreshold}px 0px`,
        threshold: 0,
      }
    );

    observer.observe(target);

    return () => {
      observer.disconnect();
    };
  }, [hasMore, isLoading, onLoadMore, scrollElement, loadMoreThreshold, refsReady]);

  // Scroll utilities
  const scrollToIndex = useCallback(
    (index: number, options?: { align?: "start" | "center" | "end" }) => {
      if (virtualizer) {
        virtualizer.scrollToIndex(index, options);
      } else if (scrollRef.current) {
        const element = scrollRef.current.children[index] as HTMLElement | undefined;
        element?.scrollIntoView({
          behavior: "smooth",
          block: options?.align || "start",
        });
      }
    },
    [virtualizer]
  );

  const scrollToTop = useCallback(() => {
    if (virtualizer) {
      virtualizer.scrollToIndex(0, { align: "start" });
    } else if (scrollRef.current) {
      scrollRef.current.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [virtualizer]);

  return {
    virtualizer,
    virtualItems,
    computedLoadingState,
    isInitialLoad,
    isLoadingMore,
    scrollRef,
    loadMoreRef,
    scrollToIndex,
    scrollToTop,
  };
}
